/* App-wide translation engine (owner 2026-07-02): a driver picks a language
 * on the landing page and EVERY screen — landing, live map, fleet dashboard,
 * dispatch office — swaps its visible text; the cab voice follows too.
 *
 * How it works: we walk the live DOM's text nodes (Weglot-style), send the
 * unseen strings to /api/translate (LLM-backed, Mongo-cached server-side),
 * and swap node text. Originals live in a WeakMap so English restores
 * perfectly. A MutationObserver catches whatever React renders later.
 */

const API = process.env.REACT_APP_BACKEND_URL;

export const LANGS = [
    { code: "en", name: "ENGLISH", tts: "en-US" },
    { code: "es", name: "ESPAÑOL", tts: "es-ES" },
    { code: "zh", name: "中文", tts: "zh-CN" },
    { code: "hi", name: "हिन्दी", tts: "hi-IN" },
    { code: "ar", name: "العربية", tts: "ar-SA" }, // RTL
    { code: "ru", name: "РУССКИЙ", tts: "ru-RU" },
];

export const getLang = () => {
    try {
        return localStorage.getItem("pcf_lang") || "en";
    } catch {
        return "en";
    }
};

export const isTranslated = () => getLang() !== "en";

export const ttsLang = () => (LANGS.find((l) => l.code === getLang()) || LANGS[0]).tts;

const orig = new WeakMap(); // node -> original English text
let cache = {}; // source -> translated (current language)
let observer = null;
let pending = new Set();
let flushTimer = null;
let activeLang = "en";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "CANVAS", "SVG", "CODE"]);

// A string is worth translating only if it has real letters — road refs,
// times, prices and symbols stay as-is (the backend keeps them intact anyway)
const hasLetters = (s) => /\p{L}/u.test(s);
const translatable = (s) => {
    if (!s) return false;
    const t = s.trim();
    return t.length >= 2 && t.length <= 400 && hasLetters(t);
};

const cacheKey = (lang) => `pcf_tr_${lang}`;
const loadLocalCache = (lang) => {
    try {
        return JSON.parse(localStorage.getItem(cacheKey(lang)) || "{}");
    } catch {
        return {};
    }
};
const saveLocalCache = (lang) => {
    try {
        // keep the device cache bounded — newest server cache always wins
        const entries = Object.entries(cache).slice(-4000);
        localStorage.setItem(cacheKey(lang), JSON.stringify(Object.fromEntries(entries)));
    } catch {}
};

const skippableNode = (node) => {
    let el = node.parentElement;
    while (el) {
        if (SKIP_TAGS.has(el.tagName)) return true;
        if (el.dataset?.noTranslate !== undefined) return true;
        if (el.classList?.contains("leaflet-tile-pane")) return true;
        if (el.isContentEditable) return true;
        el = el.parentElement;
    }
    return false;
};

const collectTextNodes = (root) => {
    const nodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
        if (skippableNode(n)) continue;
        const cur = orig.has(n) ? orig.get(n) : n.nodeValue;
        if (translatable(cur)) nodes.push(n);
    }
    return nodes;
};

const fetchTranslations = async (lang, strings) => {
    const missing = strings.filter((s) => !(s in cache));
    if (!missing.length) return;
    // Owner report 2026-10-24 (mixed-language black box): rapid globe switching
    // let an in-flight SPANISH batch land in the RUSSIAN cache. Guard twice —
    // capture the cache this fetch belongs to and only merge if the language
    // AND the cache are still current when the answer returns.
    const myCache = cache;
    try {
        const r = await fetch(`${API}/api/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lang, strings: missing }),
        });
        if (r.ok && lang === activeLang && myCache === cache) {
            const d = await r.json();
            Object.assign(cache, d.translations || {});
            saveLocalCache(lang);
        }
    } catch {}
};

const applyToNode = (node) => {
    if (!orig.has(node)) orig.set(node, node.nodeValue);
    const src = orig.get(node);
    const t = cache[src.trim()];
    if (!t) return;
    // Preserve the original's leading/trailing whitespace so layout spacing survives
    const lead = src.match(/^\s*/)[0];
    const trail = src.match(/\s*$/)[0];
    const next = lead + t + trail;
    // ONLY write when different — setting nodeValue fires a characterData
    // mutation even for an identical string, and our own observer listens for
    // exactly that; without this guard the page loops itself frozen (owner
    // report 2026-10-24: preview page would not load with a language active)
    if (node.nodeValue !== next) node.nodeValue = next;
};

const flush = async () => {
    flushTimer = null;
    const strings = [...pending].filter((s) => !(s in cache));
    pending = new Set();
    if (strings.length && activeLang !== "en") await fetchTranslations(activeLang, strings);
    // Second pass: apply whatever the fetch taught us
    collectTextNodes(document.body).forEach(applyToNode);
    translatePlaceholders();
};

const queueNodes = (nodes) => {
    if (activeLang === "en") return;
    nodes.forEach((n) => {
        const src = orig.has(n) ? orig.get(n) : n.nodeValue;
        const key = src.trim();
        if (!(key in cache)) pending.add(key);
        else applyToNode(n);
    });
    if (!flushTimer && pending.size) flushTimer = setTimeout(flush, 350);
};

const translatePlaceholders = () => {
    document.querySelectorAll("[placeholder]").forEach((el) => {
        if (skippableNode(el)) return;
        if (!el.dataset.i18nOrig) el.dataset.i18nOrig = el.getAttribute("placeholder") || "";
        const src = el.dataset.i18nOrig.trim();
        if (translatable(src) && cache[src]) el.setAttribute("placeholder", cache[src]);
    });
};

export const applyLang = async (lang) => {
    activeLang = lang;
    // speech entries are language-specific — never let yesterday's Spanish
    // voice a Russian callout; hydrate the new language's saved voice pack
    speechPackLang = null;
    hydrateSpeech(lang);
    try {
        if (lang === "en") localStorage.removeItem("pcf_lang");
        else localStorage.setItem("pcf_lang", lang);
    } catch {}
    const l = LANGS.find((x) => x.code === lang) || LANGS[0];
    document.documentElement.lang = l.tts;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    window.dispatchEvent(new CustomEvent("pcf-lang", { detail: lang }));

    if (observer) {
        observer.disconnect();
        observer = null;
    }
    // Cancel any queued batch from the previous language so its strings can't
    // be fetched-and-merged after the switch (mixed-language box fix)
    pending = new Set();
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    if (lang === "en") {
        // Restore every node we touched; clear translated placeholders
        collectTextNodes(document.body).forEach((n) => {
            if (orig.has(n)) n.nodeValue = orig.get(n);
        });
        document.querySelectorAll("[data-i18n-orig]").forEach((el) => {
            el.setAttribute("placeholder", el.dataset.i18nOrig);
            delete el.dataset.i18nOrig;
        });
        cache = {};
        return;
    }

    cache = loadLocalCache(lang);
    // Clean intermediate: restore every node to its English original first,
    // so the screen never shows leftover Spanish while Russian lands
    collectTextNodes(document.body).forEach((n) => {
        if (orig.has(n) && n.nodeValue !== orig.get(n)) n.nodeValue = orig.get(n);
    });
    // Warm from the server's shared cache in one shot — covers strings this
    // device has never seen but another driver already paid to translate.
    // SERVER WINS over the device cache: early devices cached cross-language
    // contamination (fixed above); the clean server copy heals them.
    try {
        const r = await fetch(`${API}/api/translate/${lang}`);
        if (r.ok) {
            const d = await r.json();
            cache = { ...cache, ...(d.translations || {}) };
        }
    } catch {}
    queueNodes(collectTextNodes(document.body));
    translatePlaceholders();
    await flush();

    observer = new MutationObserver((muts) => {
        const fresh = [];
        muts.forEach((m) => {
            if (m.type === "characterData" && m.target?.nodeType === 3) fresh.push(m.target);
            m.addedNodes?.forEach((an) => {
                if (an.nodeType === 3) fresh.push(an);
                else if (an.nodeType === 1) fresh.push(...collectTextNodes(an));
            });
        });
        if (fresh.length) queueNodes(fresh);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
};

// ── Voice: turn-by-turn speaks the chosen language ─────────────────────────
// Offline voice pack (owner pick 2026-10-25): when a route locks, every
// callout it can possibly speak is translated up front and persisted on the
// device — a dead zone mid-trip keeps speaking the driver's language instead
// of dropping to English. The pack is language-tagged and bounded.
const VOICE_PACK_KEY = "pcf_voicepack";
const loadVoicePack = (lang) => {
    try {
        const p = JSON.parse(localStorage.getItem(VOICE_PACK_KEY) || "null");
        return p && p.lang === lang && p.map ? p.map : {};
    } catch {
        return {};
    }
};
let speechCache = {};
let speechPackLang = null; // which language speechCache is hydrated for
const hydrateSpeech = (lang) => {
    if (speechPackLang === lang) return;
    speechCache = loadVoicePack(lang);
    speechPackLang = lang;
};
export const primeVoicePack = async (texts) => {
    const lang = getLang();
    if (lang === "en") return;
    hydrateSpeech(lang);
    const missing = [...new Set((texts || []).map((t) => String(t || "").trim()))].filter(
        (t) => translatable(t) && !speechCache[t],
    );
    if (!missing.length) return;
    for (let i = 0; i < missing.length; i += 150) {
        // server caps at 200 strings/request — stay well under
        try {
            const r = await fetch(`${API}/api/translate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ lang, strings: missing.slice(i, i + 150) }),
            });
            if (!r.ok) break;
            const d = await r.json();
            Object.entries(d.translations || {}).forEach(([k, v]) => {
                if (v) speechCache[k] = v;
            });
        } catch {
            break; // offline right now — callouts fall back to English until the next prime
        }
    }
    try {
        const entries = Object.entries(speechCache).slice(-2000);
        localStorage.setItem(VOICE_PACK_KEY, JSON.stringify({ lang, map: Object.fromEntries(entries) }));
    } catch {}
};
export const translateForSpeech = async (text) => {
    const lang = getLang();
    if (lang === "en" || !translatable(text)) return text;
    hydrateSpeech(lang);
    if (speechCache[text]) return speechCache[text];
    if (cache[text.trim()]) return cache[text.trim()];
    try {
        const r = await fetch(`${API}/api/translate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: AbortSignal.timeout(5000), // a hung translate call must never wedge the voice queue
            body: JSON.stringify({ lang, strings: [text.trim()] }),
        });
        if (r.ok) {
            const d = await r.json();
            const t = (d.translations || {})[text.trim()];
            if (t) {
                speechCache[text] = t;
                return t;
            }
        }
    } catch {}
    return text; // never block a callout on translation trouble — speak English
};

export const pickVoice = () => {
    if (!window.speechSynthesis) return null;
    const want = ttsLang();
    const voices = window.speechSynthesis.getVoices();
    return (
        voices.find((v) => v.lang === want) ||
        voices.find((v) => v.lang?.startsWith(want.split("-")[0])) ||
        null
    );
};
